import type { AppProps } from 'next/app';
import { Head } from '@zg-rentals/next/app';
// This is needed until @zillow/page-frame updates to use constellation-10
import { ThemeConstellation } from '@zillow/constellation';
import { ThemeProvider } from 'styled-components';
import { ToastProvider } from '@zillow/constellation-10';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import '@zillow/constellation-fonts/zillow-fonts.css';
import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import { ApolloClient, ApolloLink, ApolloProvider, createHttpLink, InMemoryCache } from '@apollo/client';

import '../styles/globals.css';
import { GRAPHQL_BASE_URL, GRAPHQL_ENDPOINT, ROUTES, SUPPORT_REQUEST_URL } from '../constants/urls';
import { REQUESTING_CLIENT } from '../constants/api';
import dynamic from 'next/dynamic';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { bootstrap } from '../bootstrap';
import { ErrorPage, ErrorPageContainer } from '@zg-rentals/visual-cohesion';
import { onError } from '@apollo/client/link/error';
import { logError } from '@zg-rentals/log-error';
import { useRouter } from 'next/router';
import { setClient as setCappsClient } from '@zg-rentals/ts-capps-client';
import { setClient as setIdVerificationClient } from '@zg-rentals/ts-id-verification-client';
import { setClient as setRinsClient } from '@zg-rentals/ts-rins-client';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { useComscorePageTracking } from '@zg-rentals/comscore';
import { trackComscoreEvent, trackEvent } from '../analytics';
import { useZrmPageViewEvents } from '@zg-rentals/clickstream';

const NoSsrApp = dynamic(() => import('../components/App'), { ssr: false });

setCappsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setIdVerificationClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));
setRinsClient(getClient().extend({ prefixUrl: process.env.NEXT_PUBLIC_RENTAL_PREFIX_URL }));

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      if (error.message === 'downstream dependency failure' && error.path && error.path.length > 0) {
        const httpStatus = error?.extensions?.httpStatus;
        const path = error?.path[0];
        if (httpStatus) {
          bootstrap.monitor.count({ name: 'raApiCall ' + httpStatus });
        }
        if (httpStatus && path) {
          bootstrap.monitor.count({ name: 'raApiCall ' + path + ' ' + httpStatus });
        }
        logError({ error });
      }
    });
  }
});

const zgGraphClient = new ApolloClient({
  cache: new InMemoryCache(),
  link: ApolloLink.from([
    errorLink,
    createHttpLink({
      credentials: 'include',
      headers: {
        'x-caller-id': REQUESTING_CLIENT,
        'x-z-enable-oauth-conversion': 'true',
      },
      uri: `${GRAPHQL_BASE_URL}${GRAPHQL_ENDPOINT}`,
    }),
  ]),
});

const ApplicationsRenterWeb = (appProps: AppProps) => {
  useBootstrap(() => ({ bootstrap }));

  const router = useRouter();
  useComscorePageTracking(() => trackComscoreEvent({}), router.pathname);
  useZrmPageViewEvents(trackEvent, router.pathname);

  return (
    <>
      <Head applicationName="applications-renter-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        {/* eslint-disable-next-line constellation/sentence-case */}
        <title>Rental Applications | Zillow</title>
        <link rel="preconnect" href="//www.zillowstatic.com/" crossOrigin="" />
        <link
          rel="preload"
          href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/open-sans/LATEST/open-sans-variable.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/ivar-headline/LATEST/ivar-headline-semibold-core.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        <link
          rel="preload"
          href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/ivar-headline/LATEST/ivar-headline-semibold-extended.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
      </Head>

      <RpErrorBoundary
        fallback={
          <ErrorPageContainer layout="header-nav">
            <ErrorPage
              errorMessage="Something went wrong on our end"
              errorDetails="Try refreshing the page. If that doesn’t work, check back in a few minutes."
              action={<a href={ROUTES.INDEX}>Go to applications</a>}
              secondaryAction={<a href={SUPPORT_REQUEST_URL}>Contact support</a>}
            />
          </ErrorPageContainer>
        }
      >
        <ApolloProvider client={zgGraphClient}>
          <DefaultQueryProvider
            defaultOptions={{
              queries: {
                retry: false,
                refetchOnWindowFocus: false,
                staleTime: 1000 * 60 * 30, // 30 minutes
              },
            }}
            mutationErrorContext={() => undefined} // Note: disable serializing mutation variables
          >
            <ToastProvider duration={{ info: 3000, success: 3000 }}>
              <ThemeProvider theme={ThemeConstellation}>
                <NoSsrApp appProps={appProps} />
              </ThemeProvider>
            </ToastProvider>
          </DefaultQueryProvider>
        </ApolloProvider>
      </RpErrorBoundary>
    </>
  );
};

export default ApplicationsRenterWeb;
